import React, { useState } from 'react';
import Layout from '../../../components/layout';
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ExternalLink } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import impact from "./impact.png";
import { baseUrl } from '../../../../Config/config';

const WeeklyNewsletter = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    ref: '',
    firmtype: '',
    profile_url: '',
    designation: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleRecaptcha = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const jsonData = {
        ...formData,
        recaptcha: recaptchaToken
      };

      const response = await fetch(`${baseUrl}/weeklyNewsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jsonData)
      });

      if (!response.ok) throw new Error('Failed to submit form');

      setSuccess(true);
      
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Required fields
  const requiredFields = [
    { name: 'name', type: 'text', label: 'Your Name' },
    { name: 'organization', type: 'text', label: 'Organization' },
    { name: 'email', type: 'email', label: 'Email (Official/Corporate ONLY)' },
    { name: 'phone', type: 'text', label: 'Phone' },
    { name: 'ref', type: 'text', label: 'How did you hear about us?' },
  ];

  // Optional fields
  const optionalFields = [
    { name: 'firmtype', type: 'select', label: 'Firm Type', 
      options: [
        { value: '', label: 'Select firm type' },
        { value: 'VC', label: 'Venture Capital' },
        { value: 'PE', label: 'Private Equity' },
        { value: 'IB', label: 'Investment Bank' },
        { value: 'Strategic', label: 'Strategic Investor' },
        { value: 'Other', label: 'Other' }
      ] 
    },
    { name: 'profile_url', type: 'url', label: 'LinkedIn Profile URL' },
    { name: 'designation', type: 'text', label: 'Designation' },
    { name: 'city', type: 'text', label: 'City' },
  ];

  const viewSample = () => {
    window.open('/weekly-sample.html', '_blank');
  };

  return (
    <Layout>
      <div className="min-h-screen">
        <div className="relative h-48 mb-12">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${impact})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="absolute inset-0 bg-black/20"></div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4"
          >
            <h1 className="text-5xl font-bold text-[#efefef] mb-6 relative group">
              <span className="relative">
                WEEKLY NEWSLETTER
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#c59c40] to-[#a07924] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
              </span>
            </h1>
          </motion.div>
        </div>

        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-5xl mx-auto mb-8"
          >
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
              Deal Digest Weekly Newsletter
            </h2>
            <p className="text-base text-gray-700 mb-4 leading-relaxed text-center">
              Delivered by email each Friday, the Deal Digest Weekly newsletter summarizes Private Equity / Venture Capital Transactions, IPO and M&A activity in India. 
              <button 
                onClick={viewSample}
                className="ml-1 text-[#bc8b20] hover:text-[#d6b15f] font-medium inline-flex items-center"
              >
                View Sample <ExternalLink className="ml-1 h-3 w-3" />
              </button>
            </p>
            <h3 className="text-base text-center text-gray-800 mb-6">
              Qualified Investors (PE/VC and Strategic), Advisory Firms, Founders of Entrepreneurial Companies can just complete the form below to sign up for the Free newsletter.
            </h3>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg py-8 px-6 mb-8"
          >
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Required Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                {requiredFields.map((field) => (
                  <div key={field.name} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {field.label}*
                    </label>
                    <input
                      type={field.type}
                      name={field.name}
                      required
                      value={formData[field.name]}
                      onChange={handleChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-2 border"
                    />
                  </div>
                ))}
              </div>

              {/* Optional Fields */}
              {/* <div className="mt-8">
                <h4 className="text-lg font-medium text-gray-700 mb-4">Additional Information (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                  {optionalFields.map((field) => (
                    <div key={field.name} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {field.label}
                      </label>
                      {field.type === 'select' ? (
                        <select
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-2 border"
                        >
                          {field.options.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[#c59c40] focus:ring-[#c59c40] sm:text-sm px-4 py-2 border"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div> */}

              <div className="flex justify-center mt-6">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_SITE_KEY}
                  onChange={handleRecaptcha}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full max-w-md mx-auto flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#bc8b20] hover:bg-[#d6b15f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c59c40] transition-colors duration-200"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Subscribe <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>

              {success && (
                <p className="text-green-600 text-sm text-center">
                  Successfully subscribed to the newsletter!
                </p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default WeeklyNewsletter;